import React, { useState } from 'react';
import { Plus, Minus, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { ProductImageCarousel } from './ProductImageCarousel';
import { UnavailableBadge, isUnavailable, isVariantUnavailable } from './UnavailableBadge';
import { ComingSoonButton } from './ComingSoonButton';
import { isComingSoon } from '../../utils/comingSoon';
import { VariantPicker } from './VariantPicker';
import { canSubscribeToProduct } from '../../utils/subscriptionEligibility';

/**
 * Every card in a grid is the same height: each part below has a fixed-height
 * zone, so a short name, a missing MRP or a product that can't be subscribed to
 * leaves its space empty instead of pulling the rest of the card up.
 *
 * Subscription row: `reserveSubscribe` (set by the page when the store offers
 * subscriptions and at least one listed product is eligible) keeps the row's
 * space on every card; the Subscribe button itself shows only where a
 * subscription can really be started — the store has subscriptions on and
 * marked this product as available for one — and `onSubscribe` opens the real
 * subscription setup for it. It says "Subscribe" and nothing more: a
 * subscription is charged the normal price, so it must not promise a saving.
 */
const ProductCard = ({ product, index = 0, currencySymbol, storeSettings, getItemQuantity, handleAddToCart, updateQuantity, autoPlay, detailEnabled, navigate, reserveSubscribe = false, onSubscribe }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);

  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const quantity = hasVariants
    ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0)
    : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  const goToDetail = () => {
    if (detailEnabled && navigate) navigate(`/product/${product.id}`);
  };

  const canSubscribe = reserveSubscribe && !!onSubscribe && canSubscribeToProduct(storeSettings, product);

  return (
    <div className={`product-card p-3 h-full flex flex-col stagger-${(index % 4) + 1}`} data-testid={`product-${product.id}`}>
      {discount > 0 && (
        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded z-[2]">
          {discount}% OFF
        </div>
      )}
      <div className={`aspect-square shrink-0 bg-gray-50 rounded-xl mb-3 overflow-hidden relative ${detailEnabled ? 'cursor-pointer' : ''}`}
        onClick={goToDetail}>
        <ProductImageCarousel product={product} autoPlay={autoPlay} />
        {productOos && !comingSoon && <UnavailableBadge />}
      </div>
      {/* Name: always two lines tall — a one-line name leaves the second empty. */}
      <h4 className={`font-medium text-sm leading-5 h-10 shrink-0 text-[var(--theme-text-main)] line-clamp-2 mb-1 ${detailEnabled ? 'cursor-pointer' : ''}`}
        onClick={goToDetail} data-testid={`name-${product.id}`}>
        {product.name}
      </h4>
      {/* Description and unit: one line each, kept even when empty. */}
      <p className="text-[10px] leading-4 h-4 shrink-0 text-[var(--theme-text-muted)] truncate mb-1" data-testid={`desc-${product.id}`}>{product.description}</p>
      <div className="flex items-center gap-1 h-5 shrink-0 min-w-0 mb-2" data-testid={`unit-${product.id}`}>
        <p className="text-xs leading-5 text-[var(--theme-text-muted)] truncate">{activeVariant ? activeVariant.label : product.unit}</p>
        {hasVariants && !productOos && !comingSoon && (
          <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
            className="shrink-0 flex items-center gap-0.5 text-[10px] font-semibold text-[var(--theme-primary)]"
            data-testid={`change-variant-${product.id}`}>
            Change <ChevronDown className="w-3 h-3" />
          </button>
        )}
      </div>

      <VariantPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        productId={product.id}
        productName={product.name}
        variants={product.variants}
        selectedVariant={activeVariant}
        onSelect={setSelectedVariant}
        currencySymbol={currencySymbol}
        getItemQuantity={getItemQuantity}
        storeSettings={storeSettings}
      />

      {/* Price, action and subscription row, pinned to the bottom of the card.
          Their layout depends only on the card's width (.product-card-* rules
          in index.css), never on this product's text, so every card in a grid
          matches: narrow cards (the two-column grid beside the category
          sidebar) put the action on its own full-width line under the price —
          price and the − qty + stepper can't share a line there — while wide
          cards keep price and action side by side. Add, the stepper and the
          sold-out state all fill the same slot, so tapping Add never shifts
          anything. */}
      <div className="product-card-buy mt-auto">
        <div className="product-card-buy-row" data-testid={`price-row-${product.id}`}>
          <div className="product-card-price">
            <span className="shrink-0 font-bold leading-6 text-[var(--theme-text-main)]">{currencySymbol}{displayPrice}</span>
            {discount > 0 && <span className="min-w-0 truncate text-xs leading-4 text-[var(--theme-text-muted)] line-through">{currencySymbol}{displayMrp}</span>}
          </div>
          {comingSoon ? (
            <ComingSoonButton size="slot" className="product-card-buy-action product-card-coming-soon" testId={`coming-soon-${product.id}`} />
          ) : productOos ? (
            <div className="product-card-buy-action h-8 rounded-full bg-gray-100 text-gray-400 text-xs font-medium flex items-center justify-center"
              data-testid={`sold-out-${product.id}`}>
              Sold out
            </div>
          ) : (
            <div className="product-card-buy-action min-w-0 h-8" data-testid={`cart-action-${product.id}`}>
              {quantity === 0 ? (
                <Button size="sm"
                  onClick={() => hasVariants ? handleAddToCart(product, activeVariant) : handleAddToCart(product)}
                  disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
                  className="bg-[var(--theme-primary)] text-white rounded-full w-full px-0 h-8"
                  data-testid={`add-${product.id}`}>
                  Add
                </Button>
              ) : (
                <div className="flex items-center w-full h-8 bg-[var(--theme-primary)] rounded-full">
                  {/* − and + each fill their half of the pill (icons kept at the
                      ends) so the tap targets are as large as the pill allows. */}
                  <button onClick={() => updateQuantity(cartKey, quantity - 1)}
                    className="flex-1 min-w-0 h-8 pl-1.5 flex items-center justify-start text-white" data-testid={`minus-${product.id}`}>
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="shrink-0 min-w-[20px] text-white font-medium text-sm text-center" data-testid={`qty-${product.id}`}>{quantity}</span>
                  <button onClick={() => updateQuantity(cartKey, quantity + 1)}
                    className="flex-1 min-w-0 h-8 pr-1.5 flex items-center justify-end text-white" data-testid={`plus-${product.id}`}>
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {reserveSubscribe && (
          <div className="product-card-subscribe" data-testid={`subscribe-slot-${product.id}`}>
            {canSubscribe && (
              <button onClick={() => onSubscribe(product)}
                className="w-full h-full px-2 rounded-full bg-amber-50 text-amber-700 text-xs leading-4 font-medium text-center"
                data-testid={`subscribe-${product.id}`}>
                Subscribe
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { ProductCard };
